// Colour Me Mama — Instagram Story card renderer.
// 1080x1920 JPG. Uses site fonts (Cormorant Garamond, Jost) and the EXACT
// /logo.png asset — never recreated typographically.
import { COLOUR_TOKENS } from "./copy";

const W = 1080;
const H = 1920;
const CREAM = "#F7F1E8";
const INK   = "#2E2620";

async function ensureFonts() {
  if (!("fonts" in document)) return;
  try {
    await Promise.all([
      document.fonts.load("italic 700 168px 'Cormorant Garamond'"),
      document.fonts.load("italic 500 60px 'Cormorant Garamond'"),
      document.fonts.load("italic 400 56px 'Cormorant Garamond'"),
      document.fonts.load("italic 400 48px 'Cormorant Garamond'"),
      document.fonts.load("500 44px 'Jost'"),
      document.fonts.load("500 34px 'Jost'"),
      document.fonts.load("500 28px 'Jost'"),
    ]);
  } catch { /* no-op */ }
}

let _logoPromise = null;
function loadLogo() {
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = "/logo.png";
  });
  return _logoPromise;
}

// Word-wrap helper
function wrap(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  const lines = [];
  for (const w of words) {
    const testLine = line ? line + " " + w : w;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = testLine;
    }
  }
  if (line) lines.push(line);
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return lines.length * lineHeight;
}

// Draw tracked caps by letter-spacing manually
function drawTracked(ctx, text, x, y, tracking) {
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + tracking;
  }
  return cx - x;
}
function measureTracked(ctx, text, tracking) {
  let w = 0;
  for (const ch of text) w += ctx.measureText(ch).width + tracking;
  return w - tracking;
}

export async function renderStoryCard(primary, secondary) {
  await ensureFonts();
  const logo = await loadLogo().catch(() => null);
  const P = COLOUR_TOKENS[primary];
  const S = COLOUR_TOKENS[secondary];

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Primary colour background
  ctx.fillStyle = P.hex;
  ctx.fillRect(0, 0, W, H);

  // Subtle grain
  ctx.save();
  ctx.globalAlpha = 0.06;
  ctx.fillStyle = P.ink;
  for (let i = 0; i < 900; i++) {
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
  ctx.restore();

  const ink = P.ink; // cream on dark bgs, ink-brown on light bgs

  // ---------------- Header: "COLOUR ME MAMA!" prominent, centered ----------------
  ctx.fillStyle = ink;
  ctx.textBaseline = "top";
  ctx.font = "500 44px 'Jost', sans-serif";
  const headerText = "COLOUR ME MAMA!";
  const headerTracking = 6;
  const headerW = measureTracked(ctx, headerText, headerTracking);
  const headerX = (W - headerW) / 2;
  const headerY = 110;
  drawTracked(ctx, headerText, headerX, headerY, headerTracking);
  // Small rule underneath
  ctx.strokeStyle = ink;
  ctx.globalAlpha = 0.35;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 60, headerY + 78);
  ctx.lineTo(W / 2 + 60, headerY + 78);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // ---------------- Hero: "I'm a [Primary] mama" ----------------
  const leftPad = 90;
  ctx.fillStyle = ink;
  ctx.globalAlpha = 0.75;
  ctx.font = "italic 400 56px 'Cormorant Garamond', serif";
  ctx.fillText("I'm a", leftPad, 340);
  ctx.globalAlpha = 1;

  ctx.font = "italic 700 168px 'Cormorant Garamond', serif";
  ctx.fillText(`${P.label} mama`, leftPad, 400);

  // ---------------- Secondary: "with a touch of [Chip]" ----------------
  const withY = 620;
  ctx.font = "italic 400 60px 'Cormorant Garamond', serif";
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = ink;
  ctx.fillText("with a touch of", leftPad, withY);
  ctx.globalAlpha = 1;

  const withWidth = ctx.measureText("with a touch of").width;
  ctx.font = "italic 500 56px 'Cormorant Garamond', serif";
  const chipText = S.label;
  const chipTextWidth = ctx.measureText(chipText).width;
  const chipPadX = 34;
  const chipH = 78;
  const chipW = chipTextWidth + chipPadX * 2;
  const chipX = leftPad + withWidth + 26;
  const chipY = withY - 6;
  ctx.fillStyle = S.hex;
  const r = chipH / 2;
  ctx.beginPath();
  ctx.moveTo(chipX + r, chipY);
  ctx.arcTo(chipX + chipW, chipY, chipX + chipW, chipY + chipH, r);
  ctx.arcTo(chipX + chipW, chipY + chipH, chipX, chipY + chipH, r);
  ctx.arcTo(chipX, chipY + chipH, chipX, chipY, r);
  ctx.arcTo(chipX, chipY, chipX + chipW, chipY, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = S.ink;
  ctx.textBaseline = "middle";
  ctx.fillText(chipText, chipX + chipPadX, chipY + chipH / 2 + 3);
  ctx.textBaseline = "top";

  // ---------------- Personality descriptor (primary tagline) ----------------
  ctx.fillStyle = ink;
  ctx.globalAlpha = 0.85;
  ctx.font = "italic 400 48px 'Cormorant Garamond', serif";
  wrap(ctx, P.tagline, leftPad, 800, W - leftPad * 2, 62);
  ctx.globalAlpha = 1;

  // ---------------- Colour swatches row ----------------
  const colours = ["RED", "ORANGE", "YELLOW", "GREEN", "BLUE", "PURPLE", "PINK"];
  const swatchY = 990;
  const swatchSize = 44;
  const gap = 30;
  const totalW = swatchSize * colours.length + gap * (colours.length - 1);
  let sx = (W - totalW) / 2;
  colours.forEach((c) => {
    const isActive = c === primary || c === secondary;
    ctx.beginPath();
    ctx.arc(sx + swatchSize / 2, swatchY, swatchSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = COLOUR_TOKENS[c].hex;
    ctx.globalAlpha = isActive ? 1 : 0.55;
    ctx.fill();
    if (isActive) {
      ctx.strokeStyle = ink;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(sx + swatchSize / 2, swatchY, swatchSize / 2 + 10, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    sx += swatchSize + gap;
  });

  // ---------------- Invitation on primary bg ----------------
  ctx.fillStyle = ink;
  ctx.font = "italic 400 58px 'Cormorant Garamond', serif";
  ctx.globalAlpha = 0.9;
  ctx.textAlign = "center";
  ctx.fillText("Every mama has a colour.", W / 2, 1170);
  ctx.font = "italic 500 66px 'Cormorant Garamond', serif";
  ctx.globalAlpha = 1;
  ctx.fillText("What's yours?", W / 2, 1244);
  ctx.textAlign = "left";

  // ---------------- Cream footer strip (compact signature) with logo + CTA + URL ----------------
  const stripTop = 1640;
  const stripH = H - stripTop;
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, stripTop, W, stripH);

  // Deckled top edge — subtle noise line
  ctx.save();
  ctx.globalAlpha = 0.08;
  ctx.fillStyle = INK;
  for (let x = 0; x < W; x += 4) {
    const jitter = Math.random() * 3;
    ctx.fillRect(x, stripTop - jitter, 3, 3);
  }
  ctx.restore();

  // "TAKE THE COLOUR ME MAMA QUIZ" — tracked caps
  ctx.fillStyle = INK;
  ctx.font = "500 28px 'Jost', sans-serif";
  const ctaText = "TAKE THE COLOUR ME MAMA QUIZ";
  const ctaTracking = 5;
  const ctaW = measureTracked(ctx, ctaText, ctaTracking);
  const ctaY = stripTop + 34;
  drawTracked(ctx, ctaText, (W - ctaW) / 2, ctaY, ctaTracking);

  // URL — tracked caps, smaller, muted
  ctx.font = "500 22px 'Jost', sans-serif";
  ctx.globalAlpha = 0.65;
  const urlText = "THESAFEMAMA.IN";
  const urlTracking = 6;
  const urlW = measureTracked(ctx, urlText, urlTracking);
  drawTracked(ctx, urlText, (W - urlW) / 2, ctaY + 44, urlTracking);
  ctx.globalAlpha = 1;

  // Logo — exact PNG asset, centered, respecting aspect ratio
  if (logo) {
    const maxLogoW = 480;
    const maxLogoH = 150;
    const ar = logo.width / logo.height;
    let lw = maxLogoW;
    let lh = lw / ar;
    if (lh > maxLogoH) { lh = maxLogoH; lw = lh * ar; }
    const lx = (W - lw) / 2;
    const ly = stripTop + stripH - lh - 24;
    ctx.drawImage(logo, lx, ly, lw, lh);
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

export async function downloadStoryCard(primary, secondary, filename) {
  const blob = await renderStoryCard(primary, secondary);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `colour-me-mama-${primary.toLowerCase()}-${secondary.toLowerCase()}.jpg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export async function getStoryCardObjectUrl(primary, secondary) {
  const blob = await renderStoryCard(primary, secondary);
  return URL.createObjectURL(blob);
}
